import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import starIcon from '../../../assets/dashboard_assets/star.png';

const FALLBACK_ITEMS = [
    { key: 'listings', label: 'Listings', count: 0 },
    { key: 'listing_scores', label: 'Listing Score', count: 0 },
    { key: 'deal_tag_checks', label: 'Deal Tag & Shipping Timeline Checks', count: 0 },
    { key: 'catalogue_fixes', label: 'Catalogue Health Fixes', count: 0 },
];

const CreditsBalance = () => {
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(true);

    const credits = authCtx.user?.credits ?? 0;
    const listingsEquivalent = Math.floor(credits / 50);
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

    useEffect(() => {
        if (!authCtx.token) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_URL}/auth/usage-this-month`, {
                    headers: { Authorization: `Bearer ${authCtx.token}` },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (!cancelled) setUsage(data);
            } catch (e) {
                if (!cancelled) setUsage(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [authCtx.token, API_URL]);

    const items = usage?.items?.length ? usage.items : FALLBACK_ITEMS;

    return (
        <div className="w-[417px] px-5 pt-8 pb-8 bg-white/80 shadow-[0_0_134px_14px_rgba(120,179,243,0.06)] rounded-3xl outline outline-1 outline-[#D0E7FF] flex flex-col gap-8">
            {/* Credits Balance */}
            <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                    <span className="text-[#2766AA] text-sm font-semibold font-sans opacity-70 uppercase">Credits Balance</span>
                    <div className="flex items-center gap-2">
                        <img src={starIcon} alt="credits" className="w-6 h-6" />
                        <span className="text-[#3A3A3A] text-[28px] font-medium font-sans">{credits.toLocaleString()}</span>
                    </div>
                    <span className="text-[#737373] text-sm font-medium leading-6 font-sans">
                        Outside your monthly quota · No expiry
                    </span>
                </div>
                <div className="flex flex-col gap-2">
                    <span className="text-[#737373] text-sm font-medium leading-6 font-sans">
                        Credits are used first before your monthly quota kicks in.
                    </span>
                    <span className="text-[#737373] text-sm font-bold leading-6 font-sans">
                        {credits.toLocaleString()} credits ≈ {listingsEquivalent.toLocaleString()} listing{listingsEquivalent === 1 ? '' : 's'}
                    </span>
                </div>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-[#D0E7FF]" />

            {/* This month's usage */}
            <div className="flex flex-col gap-4">
                <h4 className="text-[#3A3A3A] text-base font-semibold font-sans">This month's usage</h4>
                <div className="flex flex-col gap-3">
                    {items.map((item) => (
                        <div key={item.key || item.label} className="w-full flex justify-between items-center">
                            <span className="text-[#737373] text-sm font-medium font-sans">{item.label}</span>
                            <span className="text-[#3A3A3A] text-sm font-semibold font-sans">
                                {loading ? '—' : Number(item.count || 0).toLocaleString()}
                            </span>
                        </div>
                    ))}
                </div>
                <button
                    onClick={() => navigate('/dashboard/usage-history')}
                    className="text-[#2766AA] text-sm font-semibold font-sans hover:underline cursor-pointer bg-transparent border-none p-0 text-left"
                >
                    View history →
                </button>
            </div>
        </div>
    );
};

export default CreditsBalance;
